// Cabling: a run of conductor whose sprite is a function of what it is joined
// to.
//
// Everything electrical about a cable is already covered by rules that existed
// before it did -- it is `conduit.conductive`, so a network spreads across it by
// adjacency and anything resting on it joins by contact; it is capacity 0, so it
// carries a source's reading to the far end without draining it; it is `rust`, so
// enough Entropy corrodes it and it stops conducting. Nothing in this module
// touches any of that, and a Cabling tile with this module deleted would behave
// identically. What is here is only the **look**: which of the sixteen pieces
// (isolated stub, straight, corner, tee, cross) to draw, in whichever of the two
// palettes its corrosion stage puts it.
//
// Why that is worth a module at all: a cable that always drew the same straight
// piece would be a run whose *shape* the player has to infer from the tiles it
// happens to sit on, and the one thing a circuit puzzle most needs to be legible
// is where the circuit goes. It is also, deliberately, the only thing a cable
// tells you -- a live run and a dead one are drawn identically, because "is
// there power in this line" is what the Lamp and the Terminal are for, and a
// world that answers it twice has stopped being worth reading.
//
// **A neighbour counts when it is joined, not when it is adjacent.** The mask is
// computed over the same `isConductive` objects `queries.chargeNetwork` spreads
// across, and a *rusted* one drops out of both at once -- so corroding a cable
// mid-run visibly breaks the drawn line at exactly the tile where the circuit
// broke. That is the Puzzle Charter's "hidden state needs a visible proxy"
// arriving for free, out of the two rules already being the same rule.
//
// **A Metal Block counts too**, since 2026-08-18: a block shoved against the end
// of a run really is joined to it, so the run draws a stub into it and the
// picture keeps saying where the circuit goes. Before then the mask asked the
// narrower `isConductiveFixture`, because the walk did -- a cable beside a block
// drew as though the run ended, and it did. Both changed together, which is the
// only way this stays one claim rather than two.
//
// Flowing water is the one conductive substance deliberately left out: a river
// genuinely does join a metal grid electrically (see
// `queries.isConductiveSubstance`), but a cable drawn sprouting a stub into a
// riverbed reads as plumbing rather than as wiring, and the electrical claim is
// already made by the Lamp at the end of the line.
import { ObjectDef, RoomDef } from './types';
import { PhysicsConfig } from './physics';
import { SimEvents } from './events';
import { WorldState } from './state';
import { applyStagePresentation } from './presentation';
import { CABLE, rustStage } from './rust';
import { isConductive } from './queries';

/** The four directions a cable piece can reach, in the order their letters are
 * concatenated into a texture key. Fixed so that a key is stable: `ns` is always
 * `ns` and never `sn`. */
const CABLE_DIRS: { letter: string; dx: number; dy: number }[] = [
  { letter: 'n', dx: 0, dy: -1 },
  { letter: 'e', dx: 1, dy: 0 },
  { letter: 's', dx: 0, dy: 1 },
  { letter: 'w', dx: -1, dy: 0 },
];

/** Every shape suffix, in mask order -- `none`, `n`, `e`, `ne`, ... `nesw`.
 *
 * Exported because the renderer generates one texture per entry and the object
 * catalog lists them, and neither should be carrying its own copy of a list this
 * one can produce. */
export const CABLE_SHAPES: string[] = Array.from({ length: 16 }, (_, mask) => {
  const suffix = CABLE_DIRS.filter((_dir, i) => mask & (1 << i))
    .map((dir) => dir.letter)
    .join('');
  return suffix || 'none';
});

/** Both palettes, every shape: the closed set of texture keys a Cabling tile can
 * ever wear. */
export const CABLE_TEXTURES: string[] = [
  ...CABLE_SHAPES.map((shape) => `object-cable-${shape}`),
  ...CABLE_SHAPES.map((shape) => `object-cable-rusted-${shape}`),
];

/** Which shape this cable currently reads as -- `none` through `nesw`.
 *
 * A neighbour counts when `queries.isConductive` says so, which is the metal
 * half of the very predicate the network walk spreads across -- not a second
 * opinion about it. A rusted conductor, or one behind an open breaker, fails it
 * and so drops out of the drawn run and the circuit together. */
export function cableShape(room: RoomDef, obj: ObjectDef, physics: PhysicsConfig): string {
  const suffix = CABLE_DIRS.filter((dir) =>
    room.objects.some((o) => o.id !== obj.id && o.x === obj.x + dir.dx && o.y === obj.y + dir.dy && isConductive(o, physics)),
  )
    .map((dir) => dir.letter)
    .join('');
  return suffix || 'none';
}

/** The texture key for one cable: its corrosion stage's palette, cut to the
 * piece that matches its neighbours. */
export function cableTexture(room: RoomDef, obj: ObjectDef, physics: PhysicsConfig): string {
  const rusted = rustStage(obj, physics) === 'rusted';
  return `object-cable${rusted ? '-rusted' : ''}-${cableShape(room, obj, physics)}`;
}

/** The declared system's own body (see settle.ts): re-derives every cable's
 * name, texture and description.
 *
 * The name and description come straight from `rust.ts`'s `CABLE` rows, so a
 * corroded cable is called and reads as a corroded one exactly as every other
 * rust object does; only the texture is this module's to decide. Run over the
 * whole room rather than over the tiles that changed, for the same reason
 * `settleRust` is: a push, a split or a cast can change what any tile's
 * neighbour *is*, and a system that tried to work out which cables that could
 * have affected would be maintaining a dependency graph to save a loop over one
 * room's objects. */
export function settleCables(world: WorldState, out: SimEvents): void {
  const room = world.rooms[world.player.roomId];
  if (!room) return;
  for (const obj of room.objects) {
    if (!obj.conduit?.cable) continue;
    const stage = CABLE[rustStage(obj, world.physics)];
    applyStagePresentation(obj, { ...stage, texture: cableTexture(room, obj, world.physics) }, out);
  }
}
